import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppContext } from '../../contexts/app-context';
import { captureError } from '../../utils/capture-error';
import {
  AllianceErrorBoundary,
  isChunkLoadError,
} from '../alliance/error-boundary';
import { AppUpdatedScreen, CrashScreen } from '../alliance/system-screens';

/**
 * Clears the App Bar title while a crash or "updated" screen stands in for a
 * page, so the bar doesn't keep the previous page's name.
 */
function ClearRouteTitle() {
  // Context
  const { dispatch } = React.useContext(AppContext);

  React.useEffect(() => {
    dispatch({ type: 'SET_ROUTE_TITLE', payload: '' });
  }, [dispatch]);

  return null;
}

/**
 * Catches render errors in a route. The shell (App Bar and sidebar) stays
 * usable and the page shows the inline crash screen. A lazy chunk that
 * failed to load after a deploy shows "Dashboard has been updated" with
 * Reload instead. Navigating to another route clears the error.
 */
export function RouteErrorBoundary(props: { children: React.ReactNode }) {
  // Hooks
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <AllianceErrorBoundary
      resetKey={location.pathname}
      onError={(error, errorInfo) =>
        captureError({ data: { error, errorInfo } })
      }
      fallback={({ error, reset }) => (
        <React.Fragment>
          <ClearRouteTitle />
          {isChunkLoadError(error) ? (
            <AppUpdatedScreen />
          ) : (
            <CrashScreen
              layout="inline"
              onRetry={reset}
              onHome={() => {
                navigate('/home');
                reset();
              }}
            />
          )}
        </React.Fragment>
      )}
    >
      {props.children}
    </AllianceErrorBoundary>
  );
}
